import { Box } from "@mui/material";
import { useMemo } from "react";
import { BoomStatCard } from "./BoomStatCard";
import { PolicyScopeNode } from "../../types";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";

interface BoomStatsProps {
  data: PolicyScopeNode[];
  exceptionsCount: number;
}

export function BoomStats({ data, exceptionsCount }: BoomStatsProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  const stats = useMemo(() => {
    let totalPass = 0;
    let totalFail = 0;
    let totalFindings = 0;
    let totalAssets = 0;

    const assetsByRisk = {
      unaffected: 0,
      none: 0,
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
    };

    data.forEach((node) => {
      const blastRadius = node.blastRadius;
      const findingStats = node.findingStats;

      if (findingStats) {
        totalPass += findingStats.pass || 0;
        totalFail += findingStats.fail || 0;
        totalFindings += findingStats.total || 0;
      }

      if (blastRadius) {
        assetsByRisk.unaffected +=
          blastRadius.assets - (blastRadius.affected || 0);
        assetsByRisk.none += blastRadius.none || 0;
        assetsByRisk.low += blastRadius.low || 0;
        assetsByRisk.medium += blastRadius.medium || 0;
        assetsByRisk.high += blastRadius.high || 0;
        assetsByRisk.critical += blastRadius.critical || 0;
        totalAssets += blastRadius.assets;
      }
    });

    const passingPercentage =
      totalFindings > 0 ? Math.round((totalPass / totalFindings) * 100) : 0;
    const failingPercentage = 100 - passingPercentage;

    return {
      passingPercentage,
      failingPercentage,
      assetsByRisk,
      totalAssets,
      totalPass,
      totalFail,
      totalFindings,
    };
  }, [data]);

  return (
    <Box sx={{ display: "flex", gap: 2, width: "100%", flexWrap: "wrap" }}>
      <BoomStatCard
        variant="percentage"
        percentage={stats.passingPercentage}
        color="none.main"
        label="Checks Passing"
      />
      <BoomStatCard
        variant="percentage"
        percentage={stats.failingPercentage}
        color="critical.main"
        label="Checks Failing"
      />
      <BoomStatCard
        variant="stacked"
        segments={[
          {
            value: stats.assetsByRisk.none + stats.assetsByRisk.unaffected,
            color: "none.main",
          },
          { value: stats.assetsByRisk.low, color: "low.main" },
          { value: stats.assetsByRisk.medium, color: "medium.main" },
          { value: stats.assetsByRisk.high, color: "high.main" },
          { value: stats.assetsByRisk.critical, color: "critical.main" },
        ]}
        label="Total Assets"
      />
      {isFeatureEnabled("Platform Policy Exceptions") && (
        <BoomStatCard
          variant="simple"
          value={exceptionsCount}
          label="Total Exceptions"
        />
      )}
    </Box>
  );
}
